import { useState, useCallback, useMemo, useEffect } from 'react'
import { Plus, Cpu, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/DataTable'
import { TechnologyFormDialog } from '../components/TechnologyFormDialog'
import { TechnologyDeleteDialog } from '../components/TechnologyDeleteDialog'
import { TechnologyViewDialog } from '../components/TechnologyViewDialog'
import { TechnologyImportDialog } from '../components/TechnologyImportDialog'
import { useTechnologies, useDeleteTechnology } from '../hooks'
import { createTechnologyColumns } from '../columns'
import { debounce } from '@/lib/utils'
import { useCan } from '@/hooks/useCan'
import type { Technology } from '@/types/entities'
import type { ListParams } from '@/lib/api/types'

export function TechnologyListPage() {
    const { t } = useTranslation()
    const [selectedTechnology, setSelectedTechnology] = useState<Technology | null>(null)
    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [showImportDialog, setShowImportDialog] = useState(false)
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showViewDialog, setShowViewDialog] = useState(false)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    // The screen is open to every technologies.read; a change is offered only where the API allows it.
    const canCreate = useCan('technologies.create')
    const canUpdate = useCan('technologies.update')
    const canDelete = useCan('technologies.delete')

    // Debounce search so we don't refetch on every keystroke
    const debouncedSetSearch = useMemo(() => debounce((value: string) => {
        setDebouncedSearch(value)
        setPage(1) // Reset to page 1 when search changes
    }, 350), [])
    // A search still waiting when the page closes is dropped, not applied to a page that is gone.
    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

    useEffect(() => {
        debouncedSetSearch(search)
    }, [search, debouncedSetSearch])

    // Memoize params to prevent unnecessary re-renders
    const params: ListParams = useMemo(() => ({
        page,
        perPage: limit,
        search: debouncedSearch || undefined
    }), [page, limit, debouncedSearch])

    const { data: technologiesResponse, isLoading } = useTechnologies(params)
    const deleteTechnology = useDeleteTechnology()

    // Memoize data extraction to prevent unnecessary recalculations
    const apiResponse = useMemo(() => technologiesResponse?.data, [technologiesResponse])
    const technologies = useMemo(() => apiResponse?.data || [], [apiResponse])
    const totalTechnologies = useMemo(() => 
        apiResponse?.pagination?.total || apiResponse?.total || 0, 
        [apiResponse]
    )

    const handleEdit = useCallback((technology: Technology) => {
        setSelectedTechnology(technology)
        setShowEditDialog(true)
    }, [])

    const handleDelete = useCallback((technology: Technology) => {
        setSelectedTechnology(technology)
        setShowDeleteDialog(true)
    }, [])

    const handleView = useCallback((technology: Technology) => {
        setSelectedTechnology(technology)
        setShowViewDialog(true)
    }, [])

    const handleDeleteConfirm = useCallback(() => {
        if (selectedTechnology) {
            deleteTechnology.mutate(selectedTechnology.id, {
                onSuccess: () => {
                    setShowDeleteDialog(false)
                    setSelectedTechnology(null)
                },
            })
        }
    }, [selectedTechnology, deleteTechnology])

    // Memoize columns to prevent re-creation
    const columns = useMemo(
        () => createTechnologyColumns(canUpdate ? handleEdit : undefined, canDelete ? handleDelete : undefined, handleView, t),
        [handleEdit, handleDelete, handleView, canUpdate, canDelete, t]
    )

    return (
        <div className="container mx-auto p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">{t('technologies.title')}</h1>
                    <p className="text-muted-foreground mt-1">
                        {t('technologies.pageDescription')}
                    </p>
                </div>
                {canCreate && (
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => setShowImportDialog(true)}>
                            <Upload className="mr-2 h-4 w-4" />
                            {t('technologies.import.importButton', 'Import')}
                        </Button>
                        <Button onClick={() => setShowCreateDialog(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            {t('technologies.createTechnology')}
                        </Button>
                    </div>
                )}
            </div>

            {/* Stats Card */}
            <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border bg-card p-6 md:col-start-2">
                    <div className="flex items-center space-x-2">
                        <Cpu className="h-5 w-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('technologies.totalTechnologies')}</p>
                            <p className="text-2xl font-bold">{totalTechnologies}</p>
                        </div>
                    </div>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={technologies}
                searchKey="model"
                searchPlaceholder={t('technologies.searchPlaceholder')}
                searchValue={search}
                onSearchChange={setSearch}
                pagination={
                    apiResponse
                        ? {
                              page: apiResponse.pagination?.page || apiResponse.page || page,
                              limit: apiResponse.pagination?.limit || apiResponse.perPage || limit,
                              total: apiResponse.pagination?.total || apiResponse.total || 0,
                              onPageChange: (newPage: number) => {
                                  setPage(newPage)
                              },
                              onPageSizeChange: (newLimit: number) => {
                                  setLimit(newLimit)
                                  setPage(1)
                              },
                          }
                        : undefined
                }
            />

            <TechnologyFormDialog
                open={showCreateDialog || showEditDialog}
                onOpenChange={(open) => {
                    if (!open) {
                        setShowCreateDialog(false)
                        setShowEditDialog(false)
                        setSelectedTechnology(null)
                    }
                }}
                technology={showEditDialog ? selectedTechnology : null}
            />

            <TechnologyImportDialog
                open={showImportDialog}
                onOpenChange={setShowImportDialog}
            />

            <TechnologyDeleteDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                technology={selectedTechnology}
                onConfirm={handleDeleteConfirm}
                isLoading={deleteTechnology.isPending}
            />

            <TechnologyViewDialog
                open={showViewDialog}
                onOpenChange={setShowViewDialog}
                technology={selectedTechnology}
            />
        </div>
    )
}
